import { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { ContratosActionsKeysEnum, urlBackend } from '@/shared/constants';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui/confirm-modal.store';
import { useUiStore } from '@/store/ui';

type SubirDocumentosDetalle = {
  id_preventa?: string;
  ok?: boolean;
  error?: string;
  subidos?: number;
};

type SubirDocumentosResData = {
  detalle?: SubirDocumentosDetalle[];
};

interface ButtonSubirDocumentosFirmadosProps {
  preventaId: string;
  ordenTrabajoId?: string;
  noTramite?: string | null;
  enableReload?: boolean;
}

/**
 * Sube los documentos ya firmados en Enext (reparar-firma/subir_documentos)
 * y, si hay orden de trabajo, genera además los anexos 1F y la Orden de Servicio
 * con firma manual (igual que el botón "Generar Documentos").
 * @param {string} preventaId - ID de la preventa.
 * @param {string} ordenTrabajoId - ID de la orden de trabajo (para generar anexos).
 * @param {string | null} noTramite - noTramite del trámite (opcional; la preventa ya lo tiene guardado).
 * @param {boolean} enableReload - recarga la página tras el éxito.
 * @returns {void}
 */
export default function ButtonSubirDocumentosFirmados({
  preventaId,
  ordenTrabajoId,
  noTramite,
  enableReload = false,
}: ButtonSubirDocumentosFirmadosProps) {
  const setAlert = useUiStore(s => s.setAlert);
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(s => s.setConfirmDialogIsOpen);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const subirDocumentos = async () => {
    setConfirmDialogIsOpen(false);
    setIsLoading(true);

    let primerError: string | null = null;

    try {
      const { post, put } = erpApi();

      const payload: any = {
        accion: 'subir_documentos',
        preventas: [preventaId],
      };
      if (noTramite) payload.noTramite = noTramite;

      const resp = await post<SubirDocumentosResData>(
        `${urlBackend}api/v1/preventas/reparar-firma/`,
        payload,
        true
      );

      const detalle = resp?.data?.detalle?.[0];
      if (resp?.code !== 200 || !detalle?.ok) {
        primerError =
          detalle?.error || resp?.message || 'No se pudieron subir los documentos firmados';
      }

      // Generar anexos 1F y Orden de Servicio (como el botón "Generar Documentos")
      if (ordenTrabajoId) {
        const resAnexos = await put<any>(
          `${urlBackend}api/v1/orden-trabajo/${ordenTrabajoId}/`,
          { generar_anexos: true, os: true },
          true
        );
        if (resAnexos?.code !== 200 && !primerError) {
          primerError =
            resAnexos?.message || 'No se pudieron generar los anexos 1F y la Orden de Servicio';
        }
      }

      if (primerError) {
        setAlert(true, primerError, AlertSeverityEnum.error);
      } else {
        // Invalidar caché de contrato para que se refresquen los datos
        queryClient.invalidateQueries({ queryKey: [ContratosActionsKeysEnum.CONTRATO] });
        
        setAlert(
          true,
          `Documentos firmados subidos correctamente${detalle?.subidos ? ` (${detalle.subidos})` : ''}`,
          AlertSeverityEnum.success
        );
        if (enableReload) {
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }
    } catch (error: any) {
      setAlert(
        true,
        error?.response?.data?.message ||
          error?.response?.data?.detail?.[0]?.error ||
          error?.message ||
          'Error al subir los documentos firmados',
        AlertSeverityEnum.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="outlined"
      disabled={isLoading}
      onClick={() => {
        setConfirmDialog({
          isOpen: true,
          title: 'Generar documentos (Firma electrónica)',
          subtitle: `¿Desea descargar los documentos firmados de Enext${
            noTramite ? ` (${noTramite})` : ''
          } y cargarlos al contrato?`,
          confirmTextBtn: 'Sí, ejecutar',
          cancelTextBtn: 'Cancelar',
          onConfirm: subirDocumentos,
        });
      }}
    >
      {isLoading ? <CircularProgress size={18} /> : 'Generar documentos (Firma electrónica)'}
    </Button>
  );
}